import { DomainError } from './errors.js';

/**
 * Personel kartının fotoğrafı ve sağlık bilgisi (sahip 2026-11-04 B1, B2; ADR 0158). Saf kurallar:
 * I/O yok.
 */

/** Fotoğraf en çok 5 MB; tarayıcı yüklemeden önce küçültür (ADR 0158 §1). */
export const CREW_PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export const CREW_PHOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type CrewPhotoMimeType = (typeof CREW_PHOTO_MIME_TYPES)[number];

const ascii = (text: string) => [...text].map((c) => c.charCodeAt(0));
const startsWith = (head: Uint8Array, bytes: readonly number[], at = 0) =>
  head.length >= at + bytes.length && bytes.every((byte, i) => head[at + i] === byte);

/**
 * Fotoğrafın türü içerikten (istemcinin bildirdiği tür ve dosya adı güvenilmez; K15 emsali): JPEG
 * `FF D8 FF`, PNG imzası, WebP `RIFF....WEBP`. Başka tür `null`. En az 12 bayt gerekir.
 */
export function sniffCrewPhotoType(head: Uint8Array): CrewPhotoMimeType | null {
  if (startsWith(head, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (startsWith(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (startsWith(head, ascii('RIFF')) && startsWith(head, ascii('WEBP'), 8)) return 'image/webp';
  return null;
}

/** Kan grubu (ABO + Rh). Türkçe yazımla `0`; arayüz etiketi dile göre. */
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', '0+', '0-'] as const;
export type BloodGroup = (typeof BLOOD_GROUPS)[number];

/**
 * Sağlık bilgisi (ADR 0158 §2): özel nitelikli kişisel veri [KVKK md. 6; GDPR md. 9; MLC A4.1 ¶2].
 * Ayrı tablo (`crew_medical_profiles`), ayrı izin (`crew_medical`), her görüntüleme kayıtlı.
 */
export interface CrewMedicalFields {
  bloodGroup: BloodGroup | null;
  medicalHistory: string | null;
  medications: string | null;
  allergies: string | null;
}

export type CrewMedicalInput = { [K in keyof CrewMedicalFields]?: string | null | undefined };

export const CREW_MEDICAL_FIELDS = [
  'bloodGroup',
  'medicalHistory',
  'medications',
  'allergies',
] as const satisfies readonly (keyof CrewMedicalFields)[];

const MEDICAL_MAX: Record<Exclude<keyof CrewMedicalFields, 'bloodGroup'>, number> = {
  medicalHistory: 4000,
  medications: 2000,
  allergies: 2000,
};

/** Serbest metin: kırpılır, satır sonları korunur, satır içi boşluk tekleşir. */
function longText(input: CrewMedicalInput, field: keyof typeof MEDICAL_MAX): string | null {
  const raw: unknown = input[field];
  if (raw === undefined || raw === null) return null;
  if (typeof raw !== 'string') throw new DomainError('VALIDATION_FAILED', field, field);
  const value = raw
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .trim();
  if (value === '') return null;
  if (value.length > MEDICAL_MAX[field]) throw new DomainError('VALIDATION_FAILED', field, field);
  return value;
}

/** Sağlık bilgisini doğrular ve biçimler; hata `VALIDATION_FAILED`, ayrıntı alan adı. */
export function parseCrewMedical(input: CrewMedicalInput): CrewMedicalFields {
  const raw: unknown = input.bloodGroup;
  let bloodGroup: BloodGroup | null = null;
  if (raw !== undefined && raw !== null && raw !== '') {
    if (typeof raw !== 'string' || !(BLOOD_GROUPS as readonly string[]).includes(raw)) {
      throw new DomainError('VALIDATION_FAILED', 'bloodGroup', 'bloodGroup');
    }
    bloodGroup = raw as BloodGroup;
  }
  return {
    bloodGroup,
    medicalHistory: longText(input, 'medicalHistory'),
    medications: longText(input, 'medications'),
    allergies: longText(input, 'allergies'),
  };
}
